import { Bar, CartesianGrid, Cell, ComposedChart, LabelList, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Box from "@mui/material/Box";
import { SERIES_COLORS } from "./palette";

export type ParetoDatum = { key: string; label: string; value: number; color: string };
export type ParetoPoint = ParetoDatum & { cumulativePct: number; isOthers: boolean };
export type ParetoChartProps = {
  data: ParetoDatum[];
  ariaLabel: string;
  /** Barras individuais; o excedente vira "Demais motivos" (Review Focus 5). */
  maxBars?: number;
  othersLabel?: string;
  thresholdPct?: number;
  selectedKey?: string | null;
  onBarClick?: (key: string) => void;
  height?: number;
  width?: number;
};

export const OTHERS_KEY = "__others__";
const OTHERS_COLOR = "#cbd5e1";
const THRESHOLD_COLOR = "#f97316";
const LABEL_MAX = 28;

export const truncateLabel = (label: string) => (label.length > LABEL_MAX ? `${label.slice(0, LABEL_MAX - 1)}…` : label);

/** Ordena por valor (desc), agrupa o excedente de `maxBars` e calcula o % acumulado. */
export function paretoSeries(data: ParetoDatum[], maxBars: number, othersLabel: string): ParetoPoint[] {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const head = sorted.slice(0, maxBars);
  const rest = sorted.slice(maxBars);
  const bars: Array<ParetoDatum & { isOthers: boolean }> = head.map((d) => ({ ...d, isOthers: false }));
  if (rest.length) bars.push({ key: OTHERS_KEY, label: othersLabel, value: rest.reduce((s, d) => s + d.value, 0), color: OTHERS_COLOR, isOthers: true });
  const total = bars.reduce((s, d) => s + d.value, 0);
  let cum = 0;
  return bars.map((d) => {
    cum += d.value;
    return { ...d, cumulativePct: total ? Math.round((cum / total) * 100) : 0 };
  });
}

// width fixo = sem ResponsiveContainer (que mede 0 no jsdom); sem width = ocupa 100% do pai.
export default function ParetoChart({
  data,
  ariaLabel,
  maxBars = 16,
  othersLabel = "Demais motivos",
  thresholdPct = 80,
  selectedKey = null,
  onBarClick,
  height = 320,
  width,
}: ParetoChartProps) {
  const series = paretoSeries(data, maxBars, othersLabel);
  const click = (_: unknown, index: number) => {
    const d = series[index];
    if (d && !d.isOthers && onBarClick) onBarClick(d.key);
  };
  const chart = (
    <ComposedChart data={series} width={width} height={height} margin={{ top: 20, right: 8, bottom: 8, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="label" interval={0} angle={-35} textAnchor="end" height={96} tick={{ fontSize: 11 }} tickFormatter={truncateLabel} />
      <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 11 }} />
      <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11 }} />
      <Tooltip formatter={(v: number, name: string) => (name === "cumulativePct" ? [`${v}%`, "% acumulado"] : [v, "Feedbacks"])} />
      <Bar yAxisId="count" dataKey="value" isAnimationActive={false} onClick={click} cursor={onBarClick ? "pointer" : undefined}>
        {series.map((d) => (
          <Cell key={d.key} fill={d.color} fillOpacity={selectedKey && d.key !== selectedKey ? 0.35 : 1} />
        ))}
        <LabelList dataKey="value" position="top" fontSize={11} />
      </Bar>
      <Line yAxisId="pct" type="linear" dataKey="cumulativePct" stroke={SERIES_COLORS[0]} strokeWidth={1.5} dot={{ r: 3 }} isAnimationActive={false} />
      {/* Depois das barras (o SVG pinta na ordem): o rótulo fica à direita, onde as barras do Pareto são baixas,
          com contorno branco para continuar legível se cruzar uma barra (V6). */}
      <ReferenceLine
        yAxisId="pct"
        y={thresholdPct}
        stroke={THRESHOLD_COLOR}
        strokeDasharray="4 4"
        label={{
          value: `${thresholdPct}% dos feedbacks (acumulado)`,
          position: "insideTopRight",
          fill: THRESHOLD_COLOR,
          fontSize: 11,
          stroke: "#fff",
          strokeWidth: 3,
          paintOrder: "stroke",
        }}
      />
    </ComposedChart>
  );
  return (
    <Box role="img" aria-label={ariaLabel}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
    </Box>
  );
}
